import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import ArrowDropUp from '@mui/icons-material/ArrowDropUp';
import {
  Badge,
  BadgeProps,
  Checkbox,
  Collapse,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { UNDEFINED_DISPLAY_KEY } from '../../../../constants';
import { GlobalFilterTypeHandle } from '../../../../context/types';
import { FilterTypes } from '../filterbar/types';
import { HierarchyItem } from '../types';
import { isChecked, itemToFilters } from '../utils';
import { SelectAllClearAllButtonGroup } from './SelectAllClearAllButtonGroup';

const StyledBadge = styled(Badge)<BadgeProps>(() => ({
  flexShrink: 'unset !important',
  '& .MuiBadge-badge': {
    right: -30,
    top: 14,
    fontWeight: '800',
    color: 'white',
    backgroundColor: 'transparent',
    border: `1px solid white`,
    padding: '0 4px',
  },
}));

export interface FilterTrayItemProps<T extends FilterTypes> {
  level: number;
  parentItem: HierarchyItem;
  filterHandle: GlobalFilterTypeHandle<T>;
  forceOpen: boolean;
}

export const FilterTrayItem = <T extends FilterTypes>(
  props: FilterTrayItemProps<T>
) => {
  const { level, parentItem, filterHandle, forceOpen } = props;
  const { t } = useTranslation();
  const [filterState, filterDispatch] = filterHandle;
  const [isOpen, setIsOpen] = useState(false);

  const checked = isChecked(parentItem, filterState.items);
  const subItems = parentItem.subItems;

  // Memoize the collapse state to prevent infinite re-renders
  const isCollapseOpen = useMemo(
    () => isOpen || forceOpen,
    [isOpen, forceOpen]
  );

  // const selectedFiltersCount = useMemo(
  //   () => (subItems ? selectedFiltersCountInHierarchy(subItems, filterState.items) : 0),
  //   [subItems, filterState.items]
  // );

  const handleListClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      setIsOpen(!isOpen);
    },
    [isOpen]
  );

  const renderName = () => {
    return (
      <ListItemText
        primaryTypographyProps={{
          variant: 'body2',
          color: 'textSecondary',
        }}
      >
        {parentItem.labelFn(t, t(UNDEFINED_DISPLAY_KEY))}
      </ListItemText>
    );
    // TODO: bring back the filter number when the CBP have discussed whether we want it or not
    // If we keep it we have to make it reactive to the timeslider range
    {
      /*<StyledBadge badgeContent={selectedFiltersCount} color="primary">
      <ListItemText
        primaryTypographyProps={{
          variant: 'body2',
          color: 'textSecondary',
        }}
      >
        {parentItem.labelFn(t, t(UNDEFINED_DISPLAY_KEY))}
      </ListItemText>
    </StyledBadge>*/
    }
  };

  return (
    <>
      <ListItem
        dense
        className="intercom_filter"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingRight: '4px',
        }}
      >
        <Grid item>
          {parentItem.isSelectable ? (
            <FormControlLabel
              control={
                <Checkbox
                  key={checked.toString()}
                  defaultChecked={checked}
                  // checked={checked}
                  tabIndex={-1}
                  disableRipple
                  sx={{
                    color: 'white',
                    '&.Mui-checked': {
                      color: 'white',
                    },
                    padding: '0 12px 0 0 !important',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    filterDispatch({
                      type: checked ? 'remove-multiple' : 'add-multiple',
                      items: itemToFilters(
                        parentItem,
                        level,
                        parentItem.labelFn(t, t(UNDEFINED_DISPLAY_KEY), true)
                      ),
                    });
                  }}
                />
              }
              label={renderName()}
            />
          ) : (
            renderName()
          )}
        </Grid>
        <Grid item onClick={handleListClick}>
          {!!subItems?.length &&
            (isCollapseOpen ? (
              <ArrowDropUp
                color="secondary"
                sx={{ cursor: 'pointer' }}
                data-cy="panalyt-filter-expand-less"
              />
            ) : (
              <ArrowDropDown
                color="secondary"
                sx={{ cursor: 'pointer' }}
                data-cy="panalyt-filter-expand-more"
              />
            ))}
        </Grid>
      </ListItem>
      {subItems && (
        <Collapse in={isCollapseOpen} timeout="auto" unmountOnExit>
          {isCollapseOpen ? (
            <List
              component={'div' as 'ul'}
              sx={{
                padding: '0 0 0 12px',
                backgroundColor: `rgba(0,0,0,${(level + 1) * 0.15})`,
              }}
              dense
            >
              {subItems[0].isSelectable ? (
                <SelectAllClearAllButtonGroup
                  subItems={subItems}
                  filterHandle={filterHandle}
                  level={level}
                />
              ) : null}
              {subItems.map((subItem) => {
                return (
                  <FilterTrayItem<T>
                    key={`filter-tray-item-${subItem.key.hash}-${level}-${subItem.itemId ?? ''}`}
                    level={level + 1}
                    parentItem={subItem}
                    filterHandle={filterHandle}
                    forceOpen={forceOpen}
                  />
                );
              })}
            </List>
          ) : null}
        </Collapse>
      )}
    </>
  );
};
